import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  OAUTH_INSTAGRAM_SYNC_ATTEMPTS,
  OAUTH_INSTAGRAM_SYNC_DELAY_MS,
  getCurrentReturnPath,
  resolveInstagramOAuthReturnPage,
} from '../utils/instagramOAuth';
import { launchInstagramOAuthRedirect } from '../utils/instagramOAuthRedirect';
import { useInstagramOAuthReturn } from './useInstagramOAuthReturn';
import { notifyErrorCustom } from '../../../utils/notify';

export const useInstagramOAuth = ({
  businessId,
  syncConnectionAndSelection,
  openPostAuthPopover,
  setIsConnecting,
  instagramNetwork,
}) => {
  const { t } = useTranslation();
  const location = useLocation();

  const syncInstagramConnectionWithRetry = useCallback(async () => {
    if (!instagramNetwork) {
      return false;
    }

    for (let attempt = 0; attempt < OAUTH_INSTAGRAM_SYNC_ATTEMPTS; attempt += 1) {
      const connected = await syncConnectionAndSelection(instagramNetwork);
      if (connected) {
        return true;
      }

      if (attempt < OAUTH_INSTAGRAM_SYNC_ATTEMPTS - 1) {
        await new Promise((resolve) => {
          setTimeout(resolve, OAUTH_INSTAGRAM_SYNC_DELAY_MS);
        });
      }
    }

    return false;
  }, [instagramNetwork, syncConnectionAndSelection]);

  useInstagramOAuthReturn({
    onSuccess: async () => {
      const connected = await syncInstagramConnectionWithRetry();
      setIsConnecting(false);

      if (connected) {
        await openPostAuthPopover(instagramNetwork);
        return;
      }

      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
    },
    onFailure: () => {
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
    },
  });

  const startInstagramOAuth = useCallback(() => {
    if (!businessId || !instagramNetwork) {
      return false;
    }

    setIsConnecting(true);

    const returnPage = resolveInstagramOAuthReturnPage(location.pathname);
    const started = launchInstagramOAuthRedirect({
      businessId,
      returnPage,
      returnPath: getCurrentReturnPath(),
    });

    if (!started) {
      setIsConnecting(false);
      notifyErrorCustom(t('chatsocket.social_media.errors.instagram_connect'));
      return false;
    }

    return true;
  }, [
    businessId,
    instagramNetwork,
    location.pathname,
    setIsConnecting,
    t,
  ]);

  return {
    startInstagramOAuth,
    clearInstagramOAuthWatchers: () => {},
  };
};

/** @deprecated Use useInstagramOAuth */
export const useInstagramOAuthPopup = useInstagramOAuth;
